import { useMemo, useRef, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Empty,
  List,
  Popconfirm,
  Segmented,
  Space,
  Tag,
  Typography,
} from "antd";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { EditOutlined } from "@ant-design/icons";
import { markdownComponents } from "../MarkdownLink";
import {
  downloadLeadershipBriefDocx,
  downloadLeadershipBriefPptx,
  generateBrief,
  updateBrief,
} from "../../lib/api";
import { useAuth } from "../../lib/auth";
import type { WorkflowBrief } from "../intakeDetails/types";
import BriefEditor from "./BriefEditor";

interface Props {
  intakeId: string;
  briefType: "ai_advisory_board" | "leadership";
  briefs: WorkflowBrief[];
  onGenerated: () => void;
  /** Optional human-readable intake name; used for the downloaded filename
   *  on the leadership Word/PowerPoint exports. Falls back to intakeId. */
  intakeName?: string;
}

const TYPE_LABELS: Record<Props["briefType"], string> = {
  ai_advisory_board: "AI Advisory Board brief",
  leadership: "Executive leadership brief",
};

async function downloadBriefAsPdf(node: HTMLElement, filename: string) {
  const [jsPdfMod, html2canvasMod] = await Promise.all([
    import("jspdf"),
    import("html2canvas"),
  ]);
  const canvas = await html2canvasMod.default(node, {
    scale: 2,
    backgroundColor: "#ffffff",
  });
  const pdf = new jsPdfMod.default({ unit: "pt", format: "letter" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let position = 0;
  let heightLeft = imgHeight;
  const dataUrl = canvas.toDataURL("image/png");
  pdf.addImage(dataUrl, "PNG", 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;
  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(dataUrl, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }
  pdf.save(filename);
}

export default function BriefViewer({
  intakeId,
  briefType,
  briefs,
  onGenerated,
  intakeName,
}: Props) {
  const { message } = App.useApp();
  const { canWrite } = useAuth();
  const filteredBriefs = useMemo(
    () => briefs.filter((brief) => brief.briefType === briefType),
    [briefs, briefType]
  );
  const [selectedId, setSelectedId] = useState<string>(filteredBriefs[0]?.id ?? "live");
  const [streamedMarkdown, setStreamedMarkdown] = useState("");
  const [streamedThoughts, setStreamedThoughts] = useState("");
  const [generating, setGenerating] = useState(false);
  // OAIP-76: edit mode swaps the read-only react-markdown render for the
  // TipTap WYSIWYG editor. Only available on a saved (non-Live) brief.
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const renderedRef = useRef<HTMLDivElement>(null);

  const selectedBrief =
    selectedId === "live"
      ? null
      : filteredBriefs.find((brief) => brief.id === selectedId) || filteredBriefs[0] || null;

  // OAIP-83: warn + demote the regenerate button when the brief on screen
  // has been hand-edited, so a stray click doesn't bury the staff edits
  // under a fresh AI generation.
  const selectedBriefIsEdited = Boolean(selectedBrief?.editedAt);

  const displayMarkdown = generating || selectedId === "live"
    ? streamedMarkdown || "_Click Generate to start streaming…_"
    : selectedBrief?.markdown || "";

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      setStreamedMarkdown("");
      setStreamedThoughts("");
      setSelectedId("live");
      await generateBrief(
        { intakeId, briefType },
        {
          onDelta: (chunk) => setStreamedMarkdown((prev) => prev + chunk),
          onThought: (chunk) => setStreamedThoughts((prev) => prev + chunk),
          onMeta: (meta) => {
            if (meta?.briefId) {
              setSelectedId(meta.briefId);
            }
            message.success("Brief generated");
            onGenerated();
          },
        }
      );
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      if (!renderedRef.current || !displayMarkdown.trim()) {
        message.warning("No brief content to export.");
        return;
      }
      await downloadBriefAsPdf(renderedRef.current, `${briefType}-${intakeId}.pdf`);
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    }
  };

  const [downloadingOffice, setDownloadingOffice] = useState<"docx" | "pptx" | null>(null);

  const handleDownloadOffice = async (variant: "docx" | "pptx") => {
    if (!filteredBriefs.length) {
      message.warning("Generate a leadership brief first so the export has content.");
      return;
    }
    setDownloadingOffice(variant);
    try {
      const downloader =
        variant === "docx"
          ? downloadLeadershipBriefDocx
          : downloadLeadershipBriefPptx;
      await downloader(intakeId, intakeName || intakeId);
    } catch (error) {
      if (error instanceof Error) message.error(error.message);
    } finally {
      setDownloadingOffice(null);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card>
        <Space wrap>
          {selectedBriefIsEdited ? (
            <Popconfirm
              title="Regenerate brief?"
              description="This brief has been manually edited. Regenerating will create a new brief and make it the active version. Your edited version will remain in the brief history below."
              okText="Regenerate"
              cancelText="Keep edits"
              okButtonProps={{ danger: true }}
              onConfirm={handleGenerate}
            >
              <Button loading={generating}>Regenerate brief</Button>
            </Popconfirm>
          ) : (
            <Button type="primary" loading={generating} onClick={handleGenerate}>
              {filteredBriefs.length === 0 ? "Generate brief" : "Regenerate brief"}
            </Button>
          )}
          {briefType === "leadership" ? (
            <>
              <Button
                onClick={() => handleDownloadOffice("docx")}
                disabled={
                  filteredBriefs.length === 0 ||
                  generating ||
                  downloadingOffice !== null
                }
                loading={downloadingOffice === "docx"}
              >
                Download Word (.docx)
              </Button>
              <Button
                onClick={() => handleDownloadOffice("pptx")}
                disabled={
                  filteredBriefs.length === 0 ||
                  generating ||
                  downloadingOffice !== null
                }
                loading={downloadingOffice === "pptx"}
              >
                Download PowerPoint (.pptx)
              </Button>
            </>
          ) : (
            <Button
              onClick={handleDownloadPdf}
              disabled={!displayMarkdown.trim() || generating}
            >
              Download PDF
            </Button>
          )}
          {/* OAIP-76: edit a saved brief in TipTap. Hidden when generating
              or viewing the Live (unsaved) stream. */}
          {canWrite && selectedBrief && !generating && !editing ? (
            <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
              Edit brief
            </Button>
          ) : null}
          {filteredBriefs.length > 0 ? (
            <Segmented
              value={selectedId}
              onChange={(value) => setSelectedId(String(value))}
              options={[
                ...(streamedMarkdown ? [{ label: "Live", value: "live" }] : []),
                ...filteredBriefs.map((brief) => ({
                  label: brief.generatedAt
                    ? new Date(brief.generatedAt).toLocaleString()
                    : brief.id.slice(0, 8),
                  value: brief.id,
                })),
              ]}
            />
          ) : null}
        </Space>
        <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
          {TYPE_LABELS[briefType]}. AI generates a one-page brief from the intake transcript,
          scoring aggregate, and any matched vendors.
        </Typography.Paragraph>
      </Card>

      {streamedThoughts && generating ? (
        <Alert
          type="info"
          showIcon
          message="Thinking"
          description={
            <Typography.Paragraph
              style={{ whiteSpace: "pre-wrap", marginBottom: 0, maxHeight: 160, overflowY: "auto" }}
            >
              {streamedThoughts}
            </Typography.Paragraph>
          }
        />
      ) : null}

      <Card>
        {editing && selectedBrief ? (
          <BriefEditor
            initialMarkdown={selectedBrief.markdown || ""}
            saving={savingEdit}
            onCancel={() => setEditing(false)}
            onSave={async (next) => {
              try {
                setSavingEdit(true);
                await updateBrief(selectedBrief.id, next);
                message.success("Brief updated");
                setEditing(false);
                onGenerated();
              } catch (err) {
                message.error(err instanceof Error ? err.message : "Failed to save brief");
              } finally {
                setSavingEdit(false);
              }
            }}
          />
        ) : (
          <div ref={renderedRef} aria-live="polite" aria-busy={generating}>
            {displayMarkdown ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{displayMarkdown}</ReactMarkdown>
            ) : (
              <Empty description="No brief generated yet." />
            )}
          </div>
        )}
      </Card>

      <Card title={`Brief history (${filteredBriefs.length})`} size="small">
        {filteredBriefs.length === 0 ? (
          <Typography.Text type="secondary">No briefs in history.</Typography.Text>
        ) : (
          <List
            size="small"
            dataSource={filteredBriefs}
            renderItem={(brief) => (
              <List.Item
                actions={[
                  <Button
                    key="view"
                    type="link"
                    onClick={() => setSelectedId(brief.id)}
                  >
                    View
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space wrap>
                      <span>
                        {brief.generatedAt
                          ? new Date(brief.generatedAt).toLocaleString()
                          : brief.id}
                      </span>
                      <Tag>{brief.model}</Tag>
                    </Space>
                  }
                  description={brief.generatedByEmail || brief.generatedBy}
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </Space>
  );
}
